import './SkeletonLoader.css';

export function Skeleton({ className = '', width, height }) {
    return (
        <span
            className={`skeleton-loader ${className}`.trim()}
            style={{ width, height }}
            aria-hidden="true"
        />
    );
}

export function SkeletonCards({ count = 3, className = 'skeleton-loader-grid' }) {
    return (
        <div className={className} aria-hidden="true">
            {Array.from({ length: count }, (_, index) => (
                <div className="skeleton-loader-card" key={index}>
                    <Skeleton className="skeleton-loader-card__media" />
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--short" />
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--title" />
                    <Skeleton className="skeleton-loader-line" />
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--medium" />
                </div>
            ))}
        </div>
    );
}

export function SkeletonList({ count = 4 }) {
    return (
        <div className="skeleton-loader-list" aria-hidden="true">
            {Array.from({ length: count }, (_, index) => (
                <div className="skeleton-loader-list__row" key={index}>
                    <Skeleton className="skeleton-loader-avatar" />
                    <div className="skeleton-loader-list__copy">
                        <Skeleton className="skeleton-loader-line skeleton-loader-line--title" />
                        <Skeleton className="skeleton-loader-line skeleton-loader-line--medium" />
                    </div>
                    <Skeleton className="skeleton-loader-action" />
                </div>
            ))}
        </div>
    );
}

export function SkeletonTable({ columns = 5, rows = 5 }) {
    return (
        <div className="skeleton-loader-table" aria-hidden="true">
            <div className="skeleton-loader-table__row skeleton-loader-table__row--header" style={{ '--skeleton-columns': columns }}>
                {Array.from({ length: columns }, (_, index) => (
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--short" key={index} />
                ))}
            </div>
            {Array.from({ length: rows }, (_, rowIndex) => (
                <div className="skeleton-loader-table__row" style={{ '--skeleton-columns': columns }} key={rowIndex}>
                    {Array.from({ length: columns }, (_, columnIndex) => (
                        <Skeleton
                            className={`skeleton-loader-line ${columnIndex === 1 ? 'skeleton-loader-line--medium' : ''}`}
                            key={columnIndex}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

export function InboxSkeleton() {
    return (
        <SkeletonRegion label="Loading inbox">
            <div className="skeleton-loader-panel">
                <div className="skeleton-loader-panel__header">
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--short" />
                </div>
                <SkeletonList count={4} />
            </div>
        </SkeletonRegion>
    );
}

export function DashboardSkeleton() {
    return (
        <SkeletonRegion label="Loading dashboard" className="dashboard dashboard-skeleton">
            <aside className="sidebar dashboard-skeleton__sidebar" aria-hidden="true">
                <div className="sidebar__header">
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--title" />
                    <Skeleton className="skeleton-loader-line skeleton-loader-line--medium" />
                </div>
                <div className="dashboard-skeleton__nav">
                    {Array.from({ length: 7 }, (_, index) => (
                        <Skeleton className="dashboard-skeleton__nav-item" key={index} />
                    ))}
                </div>
            </aside>
            <main className="dashboard__content dashboard-skeleton__content" aria-hidden="true">
                <div className="dashboard-skeleton__heading">
                    <div>
                        <Skeleton className="skeleton-loader-line skeleton-loader-line--short" />
                        <Skeleton className="skeleton-loader-line skeleton-loader-line--heading" />
                    </div>
                    <Skeleton className="skeleton-loader-action" />
                </div>
                <div className="dashboard-skeleton__stats">
                    {Array.from({ length: 4 }, (_, index) => (
                        <div className="skeleton-loader-stat" key={index}>
                            <Skeleton className="skeleton-loader-line skeleton-loader-line--short" />
                            <Skeleton className="skeleton-loader-line skeleton-loader-line--value" />
                        </div>
                    ))}
                </div>
                <div className="dashboard-skeleton__panels">
                    <div className="skeleton-loader-panel"><SkeletonList count={4} /></div>
                    <div className="skeleton-loader-panel"><SkeletonList count={3} /></div>
                </div>
            </main>
        </SkeletonRegion>
    );
}

export function PageSkeleton({ label = 'Loading page', cards = 3 }) {
    return (
        <SkeletonRegion label={label} className="container py-12 page-skeleton">
            <div className="page-skeleton__heading" aria-hidden="true">
                <Skeleton className="skeleton-loader-line skeleton-loader-line--short" />
                <Skeleton className="skeleton-loader-line skeleton-loader-line--heading" />
                <Skeleton className="skeleton-loader-line skeleton-loader-line--medium" />
            </div>
            <SkeletonCards count={cards} />
        </SkeletonRegion>
    );
}

export function SkeletonRegion({ label = 'Loading content', className = '', children, ...props }) {
    return (
        <div className={className} role="status" aria-live="polite" aria-label={label} aria-busy="true" {...props}>
            {children}
            <span className="sr-only">{label}</span>
        </div>
    );
}
